/**
 * FanOutModal — Split / Rollout
 *
 * Ball holder picks one or more descendant org units and fans the request into
 * parallel child balls (canon 29 §3–4). The whole selection is one
 * catchball_fan_out call (migration 756): all children or none. Product copy
 * uses Rollout / Split — never
 * "ball tree".
 */

import { useMemo, useState } from 'react';
import { GitBranch, X } from 'lucide-react';

export interface FanOutTarget {
  id: string;
  name: string;
}

interface FanOutModalProps {
  orgUnits: FanOutTarget[];
  /** Org units that already have an in-flight child under this parent. */
  alreadyDelegatedIds?: string[];
  /** Cap from routing policy (ball_split_max_children). */
  maxChildren?: number;
  onSubmit: (targets: FanOutTarget[], requestText?: string) => Promise<void>;
  onClose: () => void;
}

export function FanOutModal({
  orgUnits,
  alreadyDelegatedIds = [],
  maxChildren = 5,
  onSubmit,
  onClose,
}: FanOutModalProps) {
  const blocked = useMemo(() => new Set(alreadyDelegatedIds), [alreadyDelegatedIds]);
  const eligible = orgUnits.filter((u) => !blocked.has(u.id));
  const remainingSlots = Math.max(0, maxChildren - alreadyDelegatedIds.length);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [requestText, setRequestText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < remainingSlots) next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selected.size === 0) return;
    if (selected.size > remainingSlots) {
      setError(`At most ${remainingSlots} more child ball${remainingSlots === 1 ? '' : 's'} allowed`);
      return;
    }
    setSubmitting(true);
    setError(null);
    const targets = eligible.filter((u) => selected.has(u.id));
    try {
      await onSubmit(targets, requestText.trim() || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fan out');
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)] dark:border-[var(--rule)] w-full max-w-md mx-4 p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-[var(--text-ter)]" />
              Fan out
            </h3>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
              Create a child ball for each team. They work in parallel; you roll up
              their proposals before passing up.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            Who receives a child ball?
          </label>
          {eligible.length === 0 ? (
            <p className="text-sm text-[var(--text-ter)] italic py-3">
              {orgUnits.length === 0
                ? 'No descendant org units available to fan out to.'
                : 'All available teams already have a child ball in this rollout.'}
            </p>
          ) : (
            <div className="max-h-56 overflow-y-auto border border-[var(--rule)] dark:border-[var(--rule)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
              {eligible.map((u) => {
                const checked = selected.has(u.id);
                return (
                  <label
                    key={u.id}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                      checked
                        ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20'
                        : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(u.id)}
                      className="rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-sky-500"
                    />
                    <span className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">{u.name}</span>
                  </label>
                );
              })}
            </div>
          )}
          {blocked.size > 0 && (
            <p className="text-xs text-[var(--text-ter)] mt-1">
              {blocked.size} team{blocked.size === 1 ? '' : 's'} already in this rollout
              (hidden).
            </p>
          )}
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            Request for each child (optional)
          </label>
          <textarea
            value={requestText}
            onChange={(e) => setRequestText(e.target.value)}
            rows={3}
            placeholder="What should each team figure out and bring back?"
            className="w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] focus:outline-none focus:ring-2 focus:ring-sky-400 resize-none"
          />
        </div>

        {error && (
          <p className="text-xs text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] dark:text-[var(--text-sec)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={selected.size === 0 || submitting}
            className="px-4 py-2 text-sm font-medium bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50 transition-colors"
          >
            {submitting
              ? 'Fanning out…'
              : selected.size === 0
                ? 'Select teams'
                : `Fan out to ${selected.size}`}
          </button>
        </div>
      </div>
    </div>
  );
}
